import type { ILogger } from "@common/core/Logger";

/**
 * Publishes a feature's activation status onto the root elements it mounted into,
 * so CSS and E2E waits can observe it.
 *
 * "Feature" here is the unit a composition root activates behind a DOM guard — not
 * a `.module.ts` root (which may activate several) and not a `components/` widget
 * (most features are services).
 */
export class FeatureStatusPublisher {
  constructor(private readonly logger: ILogger) {
    this.logger = logger.createChildLogger("FeatureStatusPublisher");
  }

  markReady(feature: string, roots: Iterable<Element>): void {
    this.setStatus(feature, roots, FEATURE_STATUS.ready);
  }

  markFailed(feature: string, roots: Iterable<Element>): void {
    this.setStatus(feature, roots, FEATURE_STATUS.failed);
  }

  private setStatus(feature: string, roots: Iterable<Element>, status: FeatureStatus): void {
    let stamped = 0;

    for (const root of roots) {
      root.setAttribute(FEATURE_STATUS_ATTRIBUTE, status);
      stamped++;
    }

    // A feature reporting status with no root element means the caller resolved its roots
    // separately from the guard that activated it — the status is then unobservable to
    // CSS and to E2E waits, which look like a hang rather than a miswiring.
    if (stamped === 0) {
      this.logger.warn(`Feature status published with no root element to stamp [${feature}]`, { feature, status });
      return;
    }

    this.logger.debug(`Feature status published [${feature}]`, { feature, status, roots: stamped });
  }
}

export const FEATURE_STATUS_ATTRIBUTE = "data-feature-status";
export const FEATURE_STATUS = {
  ready: "ready",
  failed: "failed"
} as const;
type FeatureStatus = (typeof FEATURE_STATUS)[keyof typeof FEATURE_STATUS];
